import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Tag from "@/components/base/Tag";
import { faqCategories, faqs } from "@/mocks/faqs";
import { siteInfo } from "@/mocks/site";

export default function FaqPage() {
  const [active, setActive] = useState("全部");
  const [openId, setOpenId] = useState<string>(faqs[0].id);

  const list = useMemo(
    () => (active === "全部" ? faqs : faqs.filter((item) => item.category === active)),
    [active],
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    url: `${siteInfo.url}/faq`,
    inLanguage: "zh-CN",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <Seo
        title="常见问题｜核验、来源与使用说明 - 棱镜求证"
        description="关于棱镜求证的常见问题：核验结论如何得出、案例信息来自哪里、为什么会出现反转、如何提交线索与指出错误，都在这里说明。"
        keywords="社会爆料核验常见问题,求证说明,辟谣来源,真相核验"
        path="/faq"
        jsonLd={jsonLd}
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "常见问题", path: "/faq" }]} />
          <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            常见问题
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            我们把用户最常问的问题整理在一起，包括核验方式、内容来源与使用建议。
            找不到答案的话，随时可以通过联系表单告诉我们。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto grid w-full max-w-[1240px] grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="mb-6 flex flex-wrap gap-1.5">
              {faqCategories.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setActive(item)}
                  className={`cursor-pointer rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    active === item
                      ? "bg-secondary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            {list.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center text-sm text-foreground-500">
                该分类下暂无问题，试试其他分类。
              </div>
            ) : (
              <div className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                {list.map((item) => {
                  const isOpen = openId === item.id;
                  return (
                    <div key={item.id}>
                      <button
                        type="button"
                        onClick={() => setOpenId(isOpen ? "" : item.id)}
                        aria-expanded={isOpen}
                        className="flex w-full cursor-pointer items-start justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-background-100"
                      >
                        <span className="flex-1">
                          <span className="mb-1.5 block">
                            <Tag tone="neutral">{item.category}</Tag>
                          </span>
                          <span className="font-heading text-sm font-bold text-foreground-950 md:text-base">
                            {item.question}
                          </span>
                        </span>
                        <span
                          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-base transition-colors ${
                            isOpen ? "bg-secondary-500 text-white" : "bg-background-100 text-foreground-500"
                          }`}
                          aria-hidden="true"
                        >
                          <i className={isOpen ? "ri-subtract-line" : "ri-add-line"} />
                        </span>
                      </button>
                      {isOpen ? (
                        <div className="px-5 pb-5">
                          <p className="text-sm leading-relaxed text-foreground-600">{item.answer}</p>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">还需要帮助？</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  你的问题没有出现在这里，可以直接提交给我们。附上原始链接会让我们更快核对。
                </p>
                <Link
                  to="/about#contact"
                  className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-line text-base" aria-hidden="true" />
                  <span className="whitespace-nowrap">提交线索 / 联系</span>
                </Link>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">先看看指南</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  如果你想知道自己怎么判断一条信息，求证指南把方法拆成了具体步骤。
                </p>
                <Link
                  to="/guide"
                  className="mt-4 inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-800"
                >
                  <i className="ri-book-open-line text-base" aria-hidden="true" />
                  <span className="whitespace-nowrap">阅读求证指南</span>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}